import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { XIcon } from '@/components/icons';
import { GuestCheckoutForm } from './GuestCheckoutForm';
import type { GuestCheckoutFormData } from '@/features/cart/types';

interface GuestCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: GuestCheckoutFormData) => Promise<void>;
  isSubmitting?: boolean;
}

export const GuestCheckoutModal: React.FC<GuestCheckoutModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 bg-black/90 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="relative bg-gray-950 border border-gray-800/50 rounded-2xl p-8 max-w-md w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-full text-gray-300 hover:text-white transition-all duration-200 backdrop-blur-sm"
          >
            <XIcon className="w-5 h-5" />
          </button>

          {/* Header Section */}
          <motion.div
            className="text-center mb-8"
            variants={itemVariants}
          >
            <motion.div
              className="mb-4"
              variants={itemVariants}
            >
              <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-gray-200/10 text-white">
                Guest Checkout
              </span>
            </motion.div>
            <motion.h1
              className="text-3xl lg:text-4xl font-medium text-white mb-3 tracking-tight"
              variants={itemVariants}
            >
              Enter Your Details
            </motion.h1>
            <motion.p
              className="text-gray-400 leading-relaxed"
              variants={itemVariants}
            >
              Please provide your information to continue with checkout.
            </motion.p>
          </motion.div>

          {/* Form Content */}
          <motion.div variants={itemVariants}>
            <GuestCheckoutForm
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
            />
          </motion.div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

